<template>
  <div class="voice-ai-thread">
    <div v-if="(loading || callsLoading) && !callList.length" class="voice-ai-thread__empty">Loading…</div>
    <div v-else-if="!callList.length" class="voice-ai-thread__empty">No calls yet.</div>
    <ul v-else-if="dense" class="voice-ai-thread__dense">
      <li
        v-for="row in flatCalls"
        :key="row.call.id"
        class="voice-ai-thread__drow"
        :class="{ 'voice-ai-thread__drow--child': row.depth > 0 }"
        @click.stop="openCall(row.call)">
        <i
          class="voice-ai-thread__dot"
          :class="[
            `voice-ai-thread__dot--${(row.call.status || '').toLowerCase()}`,
            { 'voice-ai-thread__dot--live': isLive(row.call.status) },
          ]" />
        <span class="voice-ai-thread__mono">{{ row.call.toPhoneNumber }}</span>
        <span class="voice-ai-thread__state">{{ statusLabel(row.call.status) }}</span>
        <span v-if="row.call.outcome" class="voice-ai-thread__outcome">{{ outcomeLabel(row.call.outcome) }}</span>
        <span v-if="isAccuracySuspect(row.call)" class="voice-ai-thread__flag">Follow-up suggested</span>
        <v-btn
          v-if="TERMINAL_STATUSES.includes(row.call.status)"
          variant="text"
          size="x-small"
          rounded="sm"
          color="primary"
          class="voice-ai-thread__again"
          @click.stop="onCallAgain(row.call)">
          Call again
        </v-btn>
        <span class="voice-ai-thread__row-time">{{ formatShort(row.call.createdOn) }}</span>
        <span v-if="dtmfSummary(row.call)" class="voice-ai-thread__ivr">{{ dtmfSummary(row.call) }}</span>
      </li>
    </ul>
    <ul v-else class="voice-ai-thread__list">
      <li v-for="call in topLevelCalls" :key="call.id" class="voice-ai-thread__row" @click.stop="openCall(call)">
        <div class="voice-ai-thread__row-main">
          <span class="voice-ai-thread__mono">{{ call.toPhoneNumber }}</span>
          <v-chip
            size="x-small"
            :color="statusColor(call.status)"
            :class="{ 'voice-ai-thread__chip--live': isLive(call.status) }">
            {{ statusLabel(call.status) }}
          </v-chip>
          <v-chip v-if="call.outcome" size="x-small" variant="outlined">{{ outcomeLabel(call.outcome) }}</v-chip>
          <v-chip
            v-if="isAccuracySuspect(call)"
            size="x-small"
            color="amber-darken-2"
            variant="tonal"
            prepend-icon="mdi-alert-circle-outline">
            Follow-up suggested
          </v-chip>
          <v-btn
            v-if="TERMINAL_STATUSES.includes(call.status)"
            size="x-small"
            variant="tonal"
            color="primary"
            class="ml-1"
            @click.stop="onCallAgain(call)">
            Call Again
          </v-btn>
        </div>
        <div class="voice-ai-thread__row-time">{{ formatDate(call.createdOn) }}</div>
        <div v-if="dtmfSummary(call)" class="voice-ai-thread__ivr">{{ dtmfSummary(call) }}</div>
        <ul v-if="childrenOf(call.id).length" class="voice-ai-thread__sublist">
          <li
            v-for="child in childrenOf(call.id)"
            :key="child.id"
            class="voice-ai-thread__row voice-ai-thread__row--child"
            @click.stop="openCall(child)">
            <span class="voice-ai-thread__mono">↳ {{ child.toPhoneNumber }}</span>
            <v-chip
              size="x-small"
              :color="statusColor(child.status)"
              :class="{ 'voice-ai-thread__chip--live': isLive(child.status) }">
              {{ statusLabel(child.status) }}
            </v-chip>
            <v-chip v-if="child.outcome" size="x-small" variant="outlined">{{ outcomeLabel(child.outcome) }}</v-chip>
            <v-chip
              v-if="isAccuracySuspect(child)"
              size="x-small"
              color="amber-darken-2"
              variant="tonal"
              prepend-icon="mdi-alert-circle-outline">
              Follow-up suggested
            </v-chip>
            <span class="voice-ai-thread__row-time">{{ formatDate(child.createdOn) }}</span>
            <div v-if="dtmfSummary(child)" class="voice-ai-thread__ivr">{{ dtmfSummary(child) }}</div>
          </li>
        </ul>
      </li>
    </ul>

    <CallTranscriptViewerDialog v-model="dialogOpen" :call="selectedCall" @call-again="onCallAgain" />
    <VoiceCallRetriggerDialog
      v-model="retriggerOpen"
      :source-call="retriggerSource"
      :tenant-config="config"
      @triggered="onRetriggered" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import CallTranscriptViewerDialog from "@/components/voice_ai/CallTranscriptViewerDialog.vue";
import {
  statusColor,
  statusLabel,
  outcomeLabel,
  TERMINAL_STATUSES,
  LIVE_POLL_MS,
  isAccuracySuspect,
  isActivelyDialing as isActivelyDialingAt,
} from "@/components/voice_ai/constants";
import VoiceCallRetriggerDialog from "@/components/voice_ai/VoiceCallRetriggerDialog.vue";
import { anchorQueryParams } from "@/components/voice_ai/anchors";
import { useVoiceAI } from "@/composables/useVoiceAI";

const props = defineProps({
  // The one case row whose calls are listed: { type: "order_info" | "intake_order" | "intake_document", id }.
  anchor: { type: Object, required: true },
  // When provided (PA expanded view), calls come from the page-level batched
  // fetch (LAM-4503) and this component fetches nothing on mount.
  calls: { type: Array, default: undefined },
  callsLoading: { type: Boolean, default: false },
  tenantConfig: { type: Object, default: undefined },
  // Flat 32px rows with a status dot instead of chips, for cards that embed the thread.
  dense: { type: Boolean, default: false },
});

const emit = defineEmits(["calls-changed"]);

const { listCalls, getConfig, loading } = useVoiceAI();
const config = ref(props.tenantConfig ?? null);
const callList = ref(props.calls ?? []);
watch(callList, (calls) => emit("calls-changed", calls), { immediate: true });
const dialogOpen = ref(false);
const selectedCall = ref(null);
const retriggerOpen = ref(false);
const retriggerSource = ref(null);

function isLive(callStatus) {
  return callStatus === "RINGING" || callStatus === "IN_PROGRESS";
}

// Compact "IVR: 0 → 2 → 3" trail of the digits the agent keyed via DTMF (from webhook metadata).
function dtmfSummary(call) {
  const presses = call?.metadata?.dtmfPresses;
  if (!Array.isArray(presses) || !presses.length) return "";
  const keys = presses.map((p) => p?.keys).filter((k) => k != null);
  return keys.length ? `IVR: ${keys.join(" → ")}` : "";
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const tz = config.value?.timezone;
  if (!tz) return d.toLocaleString();
  try {
    return d.toLocaleString("en-US", { timeZone: tz });
  } catch {
    return d.toLocaleString();
  }
}

function formatShort(iso) {
  if (!iso) return "";
  const tz = config.value?.timezone;
  const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  try {
    return new Date(iso).toLocaleString("en-US", tz ? { ...opts, timeZone: tz } : opts);
  } catch {
    return new Date(iso).toLocaleString("en-US", opts);
  }
}

const topLevelCalls = computed(() => callList.value.filter((c) => !c.parentCallSession));
function childrenOf(id) {
  return callList.value.filter((c) => c.parentCallSession === id);
}
const flatCalls = computed(() =>
  topLevelCalls.value.flatMap((call) => [
    { call, depth: 0 },
    ...childrenOf(call.id).map((child) => ({ call: child, depth: 1 })),
  ])
);

function openCall(call) {
  selectedCall.value = call;
  dialogOpen.value = true;
}

async function refresh() {
  refreshInFlight = true;
  let resp;
  try {
    resp = await listCalls(anchorQueryParams(props.anchor));
  } finally {
    refreshInFlight = false;
  }
  // DRF list endpoint may return { results, count, ... } via pagination — handle both.
  callList.value = Array.isArray(resp) ? resp : resp?.results || [];
  // Keep an open transcript dialog pointed at the fresh row so status/outcome/media
  // availability update live as the call completes.
  if (selectedCall.value) {
    const updated = callList.value.find((c) => c.id === selectedCall.value.id);
    if (updated) selectedCall.value = updated;
  }
}

// While a call is actively dialing/talking, poll on a short interval so the thread tracks it
// live (Queued → Ringing → In Progress → Completed) instead of waiting on a manual refresh.
// ONLY in self-fetch (accordion) mode: when props.calls is provided, the page owns the batched
// fetch AND the fast poll (LAM-4503) — a per-row interval here would fan out N requests.
let pollTimer = null;
let refreshInFlight = false;

// Reactive clock: the polling gate depends on wall time, which computeds can't observe. A 30s
// tick (a ref write, no network) lets a scheduled call entering its window start polling even
// when nothing else refetches the list.
const nowTick = ref(Date.now());
let tickTimer = null;

const hasActiveCall = computed(() => {
  const now = nowTick.value;
  return callList.value.some((c) => isActivelyDialingAt(c, now));
});

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

watch(
  hasActiveCall,
  (active) => {
    if (!active || props.calls !== undefined) {
      stopPolling();
      return;
    }
    if (pollTimer) return;
    pollTimer = setInterval(async () => {
      if (refreshInFlight) return;
      try {
        await refresh();
      } catch {
        // Transient fetch failure — keep polling; terminal state ends the loop via hasActiveCall.
      }
    }, LIVE_POLL_MS);
  },
  { immediate: true }
);

onMounted(() => {
  tickTimer = setInterval(() => {
    nowTick.value = Date.now();
  }, 30000);
});

onBeforeUnmount(() => {
  stopPolling();
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
});

watch(
  () => props.calls,
  (v) => {
    if (v === undefined) return;
    // The page-level batch resets to [] on a failed fetch; don't let that wipe a list with a
    // live call in it (the next successful batch tick restores truth either way).
    if (!v.length && callList.value.length && hasActiveCall.value) return;
    callList.value = v;
    if (selectedCall.value) {
      const updated = v.find((c) => c.id === selectedCall.value.id);
      if (updated) selectedCall.value = updated;
    }
  }
);
watch(
  () => props.tenantConfig,
  (v) => {
    if (v !== undefined) config.value = v;
  }
);

onMounted(async () => {
  if (props.tenantConfig === undefined) {
    try {
      config.value = await getConfig();
    } catch {
      /* non-critical */
    }
  }
  if (props.calls === undefined) await refresh();
});
watch(
  () => `${props.anchor.type}:${props.anchor.id}`,
  () => {
    if (props.calls === undefined) refresh();
  }
);

function onCallAgain(call) {
  retriggerSource.value = call;
  retriggerOpen.value = true;
}

async function onRetriggered() {
  retriggerOpen.value = false;
  retriggerSource.value = null;
  await refresh();
}

defineExpose({ refresh });
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.voice-ai-thread {
  font-size: 13px;
}
.voice-ai-thread__list,
.voice-ai-thread__sublist {
  list-style: none;
  padding: 0;
  margin: 0;
}
.voice-ai-thread__sublist {
  margin-top: 4px;
  margin-left: 12px;
  padding-left: 8px;
  border-left: 1px dashed $lh_dash_border;
}
.voice-ai-thread__row {
  padding: 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
}
.voice-ai-thread__row--child {
  padding-left: 0;
  font-size: 12px;
}
.voice-ai-thread__row-main {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.voice-ai-thread__mono {
  font-family: "JetBrains Mono", monospace;
}
.voice-ai-thread__row-time {
  font-size: 11px;
  color: $lh_color_blue_gray;
  margin-left: auto;
}
.voice-ai-thread__ivr {
  flex-basis: 100%;
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  color: $lh_color_blue_gray;
}
.voice-ai-thread__empty {
  font-size: 13px;
  color: $lh_color_blue_gray;
  opacity: 0.6;
}
.voice-ai-thread__dense {
  list-style: none;
  padding: 0;
  margin: 0;
}
.voice-ai-thread__drow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 10px;
  min-height: 32px;
  padding: 4px 0;
  font-size: 12.5px;
  color: $lh_color_navy_dark;
  border-top: 1px solid $lh_dash_border;
  cursor: pointer;

  &:hover {
    background: rgba($lh_color_navy, 0.02);
  }
}
.voice-ai-thread__drow--child {
  padding-left: 18px;
  color: $lh_color_blue_gray;
}
.voice-ai-thread__dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $lh_color_blue_gray;
  opacity: 0.55;
}
.voice-ai-thread__dot--completed {
  background: #1b7f4b;
  opacity: 1;
}
.voice-ai-thread__dot--failed {
  background: #b42318;
  opacity: 1;
}
.voice-ai-thread__dot--scheduled {
  background: transparent;
  border: 1.5px solid $lh_color_blue_gray;
  opacity: 0.8;
}
.voice-ai-thread__dot--queued,
.voice-ai-thread__dot--ringing,
.voice-ai-thread__dot--in_progress {
  background: #2f6fed;
  opacity: 1;
}
.voice-ai-thread__dot--live {
  animation: voice-ai-live-pulse 1.4s ease-in-out infinite;
}
.voice-ai-thread__state {
  font-weight: 500;
}
.voice-ai-thread__outcome {
  color: $lh_color_blue_gray;
}
.voice-ai-thread__flag {
  color: #b7791f;
  font-size: 11.5px;
}
.voice-ai-thread__again {
  margin-left: 2px;
  letter-spacing: 0;
  text-transform: none;
}
.voice-ai-thread__drow .voice-ai-thread__row-time {
  font-family: $lh_font_mono;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.voice-ai-thread__chip--live {
  animation: voice-ai-live-pulse 1.4s ease-in-out infinite;
}
@keyframes voice-ai-live-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
</style>
